/**
 * MODULE: domain/pages/frontend/utils/pageTreeUtils.ts
 * PURPOSE: Flatten page tree and compute parent/position updates for sidebar DnD
 */

import type { PageTreeNode } from '../../contracts/pages.types';

export type FlatPageTreeItem = {
  id: string;
  node: PageTreeNode;
  depth: number;
  parentId: string | null;
};

export type PageMoveUpdate = {
  pageId: string;
  parent_page_id: string | null;
  position: number;
};

export function findPageNode(nodes: PageTreeNode[], pageId: string): PageTreeNode | null {
  for (const node of nodes) {
    if (node.id === pageId) {
      return node;
    }
    const child = findPageNode(node.children, pageId);
    if (child) {
      return child;
    }
  }
  return null;
}

export function isPageDescendant(root: PageTreeNode, targetId: string): boolean {
  if (root.id === targetId) {
    return true;
  }
  return root.children.some((child) => isPageDescendant(child, targetId));
}

export function flattenPageTree(
  nodes: PageTreeNode[],
  parentId: string | null = null,
  depth = 0
): FlatPageTreeItem[] {
  const sorted = [...nodes].sort((a, b) => a.position - b.position);
  const items: FlatPageTreeItem[] = [];

  for (const node of sorted) {
    items.push({ id: node.id, node, depth, parentId });
    if (node.children.length > 0) {
      items.push(...flattenPageTree(node.children, node.id, depth + 1));
    }
  }

  return items;
}

export function getPageChildren(nodes: PageTreeNode[], parentId: string | null): PageTreeNode[] {
  if (parentId === null) {
    return [...nodes].sort((a, b) => a.position - b.position);
  }
  const parent = findPageNode(nodes, parentId);
  return parent ? [...parent.children].sort((a, b) => a.position - b.position) : [];
}

function assignSiblingPositions(
  siblings: PageTreeNode[],
  parentId: string | null,
  updates: PageMoveUpdate[]
): void {
  siblings.forEach((sibling, index) => {
    updates.push({
      pageId: sibling.id,
      parent_page_id: parentId,
      position: index + 1,
    });
  });
}

export function computePageMove(
  tree: PageTreeNode[],
  activeId: string,
  overId: string,
  options?: { nestAsChild?: boolean }
): PageMoveUpdate[] | null {
  if (activeId === overId) {
    return null;
  }

  const activeNode = findPageNode(tree, activeId);
  const overNode = findPageNode(tree, overId);
  if (!activeNode || !overNode) {
    return null;
  }

  if (isPageDescendant(activeNode, overId)) {
    return null;
  }

  const updates: PageMoveUpdate[] = [];
  const oldParentId = activeNode.parent_page_id;
  const nestAsChild = options?.nestAsChild === true;
  const newParentId = nestAsChild ? overId : overNode.parent_page_id;

  const oldSiblings = getPageChildren(tree, oldParentId).filter((child) => child.id !== activeId);
  assignSiblingPositions(oldSiblings, oldParentId, updates);

  let newSiblings = getPageChildren(tree, newParentId).filter((child) => child.id !== activeId);

  if (nestAsChild) {
    newSiblings = [...newSiblings, activeNode];
  } else {
    const overIndex = newSiblings.findIndex((child) => child.id === overId);
    const insertAt = overIndex >= 0 ? overIndex : newSiblings.length;
    newSiblings.splice(insertAt, 0, activeNode);
  }

  assignSiblingPositions(newSiblings, newParentId, updates);

  const deduped = new Map<string, PageMoveUpdate>();
  for (const update of updates) {
    deduped.set(update.pageId, update);
  }
  return [...deduped.values()];
}

export function applyPageMoveToTree(
  tree: PageTreeNode[],
  updates: PageMoveUpdate[]
): PageTreeNode[] {
  const updateById = new Map(updates.map((update) => [update.pageId, update]));

  const cloneNode = (node: PageTreeNode): PageTreeNode => ({
    ...node,
    children: node.children.map(cloneNode),
  });

  const allNodes = new Map<string, PageTreeNode>();

  const collect = (nodes: PageTreeNode[]) => {
    for (const node of nodes) {
      allNodes.set(node.id, cloneNode(node));
      collect(node.children);
    }
  };
  collect(tree);

  for (const [id, node] of allNodes) {
    const patch = updateById.get(id);
    if (patch) {
      node.parent_page_id = patch.parent_page_id;
      node.position = patch.position;
    }
    node.children = [];
  }

  const roots: PageTreeNode[] = [];
  for (const node of allNodes.values()) {
    if (node.parent_page_id && allNodes.has(node.parent_page_id)) {
      allNodes.get(node.parent_page_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortTree = (nodes: PageTreeNode[]): PageTreeNode[] =>
    [...nodes]
      .sort((a, b) => a.position - b.position)
      .map((node) => ({
        ...node,
        children: sortTree(node.children),
      }));

  return sortTree(roots);
}
